import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { CheckCheck, Mail, MessageSquare, RefreshCw } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }) : "-");

export default function Notifications() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [logs, setLogs] = useState([]);

  const load = async () => {
    try {
      const [n, l] = await Promise.all([api.get("/notifications"), api.get("/notification-logs")]);
      setItems(n.data);
      setLogs(l.data);
    } catch (e) { toast.error(e.friendlyMessage); }
  };

  useEffect(() => { load(); }, []);

  const markRead = async (n) => {
    await api.post(`/notifications/${n.id}/read`).catch(() => {});
    window.dispatchEvent(new Event("notifications-changed"));
    if (n.task_id) navigate(`/tasks/${n.task_id}`);
    else load();
  };

  const markAll = async () => {
    await api.post("/notifications/read-all").catch(() => {});
    window.dispatchEvent(new Event("notifications-changed"));
    load();
  };

  const retry = async (logId) => {
    try {
      const res = await api.post(`/notification-logs/${logId}/retry`);
      toast.success(`Retry result: ${res.data.status}`);
      load();
    } catch (e) { toast.error(e.friendlyMessage); }
  };

  const unread = items.filter((n) => !n.read).length;

  return (
    <div className="animate-fade-in space-y-5" data-testid="notifications-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Communication</div>
          <h1 className="mt-1 font-heading text-3xl font-extrabold tracking-tight text-slate-900">Notifications</h1>
        </div>
        {unread > 0 && (
          <Button variant="outline" data-testid="notifications-mark-all-read" onClick={markAll}>
            <CheckCheck className="mr-1.5 h-4 w-4" /> Mark all read ({unread})
          </Button>
        )}
      </div>

      <Tabs defaultValue="inbox">
        <TabsList>
          <TabsTrigger value="inbox" data-testid="notifications-tab-inbox">Inbox ({unread} unread)</TabsTrigger>
          <TabsTrigger value="delivery" data-testid="notifications-tab-delivery">Delivery Log</TabsTrigger>
        </TabsList>
        <TabsContent value="inbox">
          <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
            {items.length === 0 && <div className="px-6 py-12 text-center text-slate-400" data-testid="notifications-empty">No notifications yet.</div>}
            {items.map((n) => (
              <button
                key={n.id}
                data-testid={`notification-item-${n.id}`}
                onClick={() => markRead(n)}
                className={`flex w-full items-start gap-3 border-b border-slate-100 px-5 py-4 text-left transition-colors hover:bg-slate-50 ${!n.read ? "bg-teal-50/40" : ""}`}
              >
                <span className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${n.read ? "bg-slate-200" : "bg-teal-500"}`} />
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-semibold text-slate-800">{n.title}</span>
                  <span className="block truncate text-sm text-slate-500">{n.message}</span>
                  <span className="mt-0.5 block text-xs text-slate-400">{fmt(n.created_at)}</span>
                </span>
              </button>
            ))}
          </div>
        </TabsContent>
        <TabsContent value="delivery">
          <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
            <div className="border-b border-slate-200 bg-slate-100/80 px-5 py-3 text-xs font-semibold uppercase tracking-wider text-slate-600">
              WhatsApp / Email delivery history
              {(user.role === "owner" || user.role === "admin") ? " (all users)" : " (yours)"}
            </div>
            {logs.length === 0 && <div className="px-6 py-12 text-center text-slate-400" data-testid="delivery-log-empty">No outbound notifications sent yet.</div>}
            {logs.map((l) => (
              <div key={l.id} data-testid={`delivery-log-${l.id}`} className="flex items-center gap-4 border-b border-slate-100 px-5 py-3.5">
                <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-slate-100">
                  {l.channel === "email" ? <Mail className="h-4 w-4 text-primary" /> : <MessageSquare className="h-4 w-4 text-teal-600" />}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-medium text-slate-800">{l.subject || "Task notification"}</div>
                  <div className="text-xs text-slate-500">To {l.recipient_name} - {fmt(l.created_at)}</div>
                  {l.failure_reason && <div className="text-xs text-rose-600">{l.failure_reason}</div>}
                  {l.detail && <div className="text-xs text-slate-400">{l.detail}</div>}
                </div>
                <span className={`rounded-full px-2.5 py-0.5 text-xs font-bold ${
                  l.status === "sent" ? "bg-emerald-50 text-emerald-700"
                  : l.status === "simulated" ? "bg-sky-50 text-sky-700" : "bg-rose-50 text-rose-700"}`}>
                  {l.status}
                </span>
                {l.status === "failed" && (user.role === "owner" || user.role === "admin") && (
                  <Button size="sm" variant="outline" data-testid={`delivery-retry-${l.id}`} onClick={() => retry(l.id)}>
                    <RefreshCw className="mr-1 h-3 w-3" /> Retry
                  </Button>
                )}
              </div>
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
